import { useFormContext } from 'react-hook-form';
import { FormInputField, FormSwitchField, LocalizedText } from '@mews/framework';
import { TextKey, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { LocalizedSubsectionTitle, NewTabLink } from '@mews-commander/ui';
import { EnterpriseScopeCounterDiscriminator } from '@mews-commander/data-access';
import { NumberInputField } from '@mews-commander/form';
import { isSystemGeneratedCounter } from '../../types';
import { CounterFormatPreviewBanner } from '../CounterFormatPreviewBanner';
import { useParseNumberFormatFromString } from '../../utils';
import { CounterFormField, CounterFormValues } from './formMapping';

interface CounterFormGeneralSettingsProps {
    isEditForm?: boolean;
    type: EnterpriseScopeCounterDiscriminator;
}

export const CounterFormGeneralSettings = ({ isEditForm, type }: CounterFormGeneralSettingsProps) => {
    const { formState, watch, getValues } = useFormContext<CounterFormValues>();
    const isSystemGenerated = isSystemGeneratedCounter(type);
    const isBillCounter = type === EnterpriseScopeCounterDiscriminator.BillCounter;
    const isNameFieldDisabled = [
        EnterpriseScopeCounterDiscriminator.CorrectionBillCounter,
        EnterpriseScopeCounterDiscriminator.PaymentConfirmationBillCounter,
    ].includes(type);

    const startValue = watch(CounterFormField.StartValue, getValues('startValue')) || 0;
    const numberFormat = watch(CounterFormField.NumberFormat);

    const result = useParseNumberFormatFromString(numberFormat);

    return (
        <Stack distribution="stretch" vertical spacing="300">
            <LocalizedSubsectionTitle textKey={TextKey.GeneralSettings} />

            <FormInputField
                helper={<LocalizedText textKey={TextKey.CounterNameHelper} />}
                placeholder={<LocalizedText textKey={TextKey.CounterNamePlaceholder} />}
                label={<LocalizedText textKey={TextKey.CounterName} />}
                name={CounterFormField.Name}
                disabled={isNameFieldDisabled}
                required
                size="medium"
            />

            {!isEditForm ? (
                <NumberInputField
                    helper={<LocalizedText textKey={TextKey.StartValueHelper} />}
                    placeholder={startValue ? '' : <LocalizedText textKey={TextKey.StartValuePlaceholder} />}
                    label={<LocalizedText textKey={TextKey.StartValue} />}
                    name={CounterFormField.StartValue}
                    required
                    disabled={isSystemGenerated}
                    size="medium"
                />
            ) : null}

            <FormInputField
                helper={
                    <div>
                        <LocalizedText textKey={TextKey.NumberFormatHelper} />{' '}
                        <NewTabLink url={createGuideLink(ArticlePath.NumberFormatForCounters)}>
                            <LocalizedText textKey={TextKey.LearnMore} />
                        </NewTabLink>
                    </div>
                }
                placeholder={<LocalizedText textKey={TextKey.NumberFormatPlaceholder} />}
                label={<LocalizedText textKey={TextKey.NumberFormat} />}
                name={CounterFormField.NumberFormat}
                size="medium"
            />

            {result ? (
                <CounterFormatPreviewBanner
                    numberOfDigits={result.numberOfDigits}
                    prefix={result.prefix}
                    startValue={startValue}
                    suffix={result.suffix}
                />
            ) : null}

            {isBillCounter ? (
                <FormInputField
                    helper={<LocalizedText textKey={TextKey.BillTypeCodeHelper} />}
                    placeholder={<LocalizedText textKey={TextKey.BillTypeCodePlaceholder} />}
                    label={<LocalizedText textKey={TextKey.BillTypeCode} />}
                    name={CounterFormField.BillTypeCode}
                    size="medium"
                />
            ) : null}

            {isBillCounter ? (
                <FormSwitchField
                    label={<LocalizedText textKey={TextKey.DisplayCIDOnBillsAndInvoices} />}
                    name={CounterFormField.DisplayCID}
                />
            ) : null}

            {formState.defaultValues?.created ? (
                <FormInputField
                    label={<LocalizedText textKey={TextKey.Created} />}
                    name={CounterFormField.Created}
                    readOnly
                    disabled
                    size="medium"
                />
            ) : null}

            {formState.defaultValues?.updated ? (
                <FormInputField
                    label={<LocalizedText textKey={TextKey.Updated} />}
                    name={CounterFormField.Updated}
                    readOnly
                    disabled
                    size="medium"
                />
            ) : null}
        </Stack>
    );
};
